import { supabase } from './supabase';
import type { AiJob } from './supabase.types';

export type DeclaredLocks = {
  repo: string | null;
  paths: string[];
  dbRef: string | null;
};

export type LockAcquireResult = {
  ok: boolean;
  reason?: string | null;
  blocked_by?: string | null;
  repo?: string | null;
  path?: string | null;
  db_ref?: string | null;
};

export type AiJobPathLock = {
  id: string;
  job_id: string;
  repo: string;
  path: string;
  held: boolean;
  created_at: string;
};

function uniquePaths(paths: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of paths) {
    const p = raw.trim();
    if (!p || seen.has(p)) continue;
    seen.add(p);
    out.push(p);
  }
  return out;
}

// End of a markdown section: the next `## ` heading, or the end of the string.
// `\Z` is a Perl anchor JavaScript does not have — there it is a literal "Z", so
// the lazy body stopped at the first capital Z in the section. Touched paths is
// the last section of every spec, so it always fell back to that alternative and
// truncated at the Z in `eczzz/...`, yielding a repo and zero paths.
const SECTION_END = '(?=^##\\s|$(?![\\s\\S]))';

// A Supabase project ref: 20 lowercase alphanumerics, never all digits (that
// would be a migration timestamp). Backticked first, then bare.
function projectRef(value: string): string | null {
  for (const m of String(value || '').matchAll(/`([a-z0-9]{20})`|\b([a-z0-9]{20})\b/g)) {
    const ref = m[1] || m[2];
    if (/[a-z]/.test(ref)) return ref;
  }
  return null;
}

export function parseSpecLocks(body: string): DeclaredLocks {
  const text = (body || '').replace(/\r\n/g, '\n');
  const touched =
    text.match(new RegExp(`^##\\s*7\\.?\\s*Touched paths\\b[\\s\\S]*?${SECTION_END}`, 'im'))?.[0] ??
    text.match(new RegExp(`^##\\s*Touched paths\\b[\\s\\S]*?${SECTION_END}`, 'im'))?.[0] ??
    '';

  let repo: string | null = null;
  const repoMatch =
    touched.match(/\*\*Repo:\*\*\s*`([^`]+)`/i) ||
    text.match(/^##\s*2\.?\s*Repo lock\b[\s\S]*?\*\*Repo:\*\*\s*`([^`]+)`/im);
  if (repoMatch) repo = repoMatch[1].trim();

  let dbRef: string | null = null;
  // The DB write line is often a sentence, not a bare ref ("none on the build
  // (Tier 1); migration ships in the PR and applies to production `abc…` at
  // approval"). Only trust this line when it says none outright or names a
  // project ref; anything else falls through to the DB lock section, which is
  // where the ref is stated plainly.
  const dbWrite = touched.match(/\*\*DB write:\*\*\s*(.+)/i);
  const dbWriteValue = dbWrite ? dbWrite[1].trim().replace(/^`|`$/g, '') : null;
  const dbWriteRef = dbWriteValue ? projectRef(dbWriteValue) : null;
  if (dbWriteValue && /^(none|n\/a|-)\b/i.test(dbWriteValue) && !dbWriteRef) {
    dbRef = null;
  } else if (dbWriteRef) {
    dbRef = dbWriteRef;
  } else {
    const dbSection =
      text.match(new RegExp(`^##\\s*3\\.?\\s*DB lock\\b[\\s\\S]*?${SECTION_END}`, 'im'))?.[0] ?? '';
    const firstLines = dbSection.split('\n').slice(0, 8).join('\n');
    if (/\*\*None\*\*/i.test(firstLines) || /^\s*None\s*$/im.test(firstLines)) {
      dbRef = null;
    } else {
      const ref = dbSection.match(/`([a-z0-9]{20})`/i) || dbSection.match(/project\s+`([a-z0-9]+)`/i);
      if (ref) dbRef = ref[1];
    }
  }

  const paths: string[] = [];
  for (const line of touched.split('\n')) {
    const m = line.match(/^\s*[-*]\s+`([^`]+)`/) || line.match(/^\s*[-*]\s+(\S+)/);
    if (!m) continue;
    const p = m[1].trim();
    if (!p || /^(repo|db write):/i.test(p)) continue;
    paths.push(p);
  }

  return { repo, paths: uniquePaths(paths), dbRef };
}

export async function setDeclaredLocks(
  jobId: string,
  locks: DeclaredLocks
): Promise<void> {
  const { error } = await supabase.rpc('ai_job_set_declared_locks', {
    p_job_id: jobId,
    // Null clears a lock; the generated arg types are non-null because the SQL args have no default.
    p_repo: locks.repo as string,
    p_paths: locks.paths,
    p_db_ref: locks.dbRef as string,
  });
  if (error) throw error;
}

export async function setDeclaredLocksFromSpec(jobId: string, specBody: string): Promise<DeclaredLocks> {
  const locks = parseSpecLocks(specBody);
  await setDeclaredLocks(jobId, locks);
  return locks;
}

export async function tryAcquireJobLocks(jobId: string): Promise<LockAcquireResult> {
  const { data, error } = await supabase.rpc('ai_job_try_acquire_locks', {
    p_job_id: jobId,
  });
  if (error) throw error;
  const result = (data ?? {}) as LockAcquireResult;
  return { ...result, ok: Boolean(result.ok) };
}

export async function lockJobPaths(
  jobId: string,
  repo: string,
  paths: string[]
): Promise<LockAcquireResult> {
  const { data, error } = await supabase.rpc('ai_job_lock_paths', {
    p_job_id: jobId,
    p_repo: repo,
    p_paths: uniquePaths(paths),
  });
  if (error) throw error;
  const result = (data ?? {}) as LockAcquireResult;
  return { ...result, ok: Boolean(result.ok) };
}

export async function parkAiJob(
  jobId: string,
  reason: string,
  blockedBy?: string | null
): Promise<AiJob> {
  const { data, error } = await supabase.rpc('ai_job_park', {
    p_job_id: jobId,
    p_reason: reason,
    p_blocked_by: (blockedBy ?? null) as string,
  });
  if (error) throw error;
  return data as AiJob;
}

export async function fetchJobPathLocks(jobId: string): Promise<AiJobPathLock[]> {
  const { data, error } = await supabase
    .from('ai_job_path_locks')
    .select('id, job_id, repo, path, held, created_at')
    .eq('job_id', jobId)
    .order('path', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AiJobPathLock[];
}
